import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Loader from '@dc/ui-library/src/organisms/Loader/Loader';
import Button from '@dc/ui-library/src/molecules/Button/Button';
import fetch from 'isomorphic-fetch';
import urljoin from 'url-join';
import queryString from 'query-string';
import GiftGuideItem from './GiftGuideItem';
import GiftGuideHeader from './GiftGuideHeader';
import Helpers from './helpers';

export default class GiftGuide extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      activeTags: this.props.activeTags,
      desktop: window.matchMedia('(min-width: 1024px)').matches,
      menuExpanded: !this.props.toggleTagsOnMobile,
      sortBy: 'default',
      items: [...this.props.items],
      currentPage: 1,
      products: [],
      productsPerPage: this.props.productsPerPage
    };
    this.setActiveTags = this.setActiveTags.bind(this);
    this.setActiveTag = this.setActiveTag.bind(this);
    this.sortProducts = this.sortProducts.bind(this);
    this.toggleMenu = this.toggleMenu.bind(this);
    this.loadMoreProducts = this.loadMoreProducts.bind(this);
    this.loadProducts();
  }

  componentDidMount() {
    if (this.props.activeTags) {
      const activeTags = this.props.tags.filter(
        (tag) => this.props.activeTags.find((activeTag) => activeTag.value === tag.value)
      );
      this.setActiveTags(activeTags);
    }
  }

  setActiveTags(tags) {
    const newTags = tags[0] ? tags : [{ value: 'all' }];
    if (newTags) {
      window.appState.dispatch({
        type: 'EVENTS_CHAT_INTERACTION', // simply push new event
        payload: {
          eventName: 'GiftGuide Interaction - Filter',
          eventAction: 'GiftGuide',
          trackFunctionArgs: 'GiftGuide Interaction',
          data: {
            tag: newTags.map((t) => t.value).join('|')
          }
        }
      });
      const tag = newTags[0];
      if (tag && !tag.isExternalLink) {
        this.setState((state) => {
          const filteredItems = this.getFilteredItems(newTags, state.currentPage);
          return {
            ...state,
            activeTags: newTags.map((t) => ({ ...t, isActive: true })),
            content: (tag.isStatic) ? this.props.inspirationContent : null,
            ...filteredItems
          };
        });
        const urlQuery = queryString.parse(window.location.search);
        urlQuery.tags = newTags.map((t) => t.value);
        const newPath = `?${queryString.stringify(urlQuery)}`;
        window.history.pushState({ path: newPath }, '', newPath);
      } else if (tag && tag.isExternalLink) {
        window.open(tag.value, '_blank');
      }
    }
  }

  setActiveTag(tag) {
    this.setActiveTags([tag]);
  }

  getSortedItems(items, sortMethod = 'default') {
    switch (sortMethod) {
      case 'l2h':
        return items.sort(Helpers.sortByLowestPrice);
      case 'h2l':
        return items.sort(Helpers.sortByHighestPrice);
      case 'default':
        return items.sort(Helpers.sortByDisplayed);
      default:
        return items;
    }
  }

  getFilteredItems(tags, currentPage) {
    const filteredItems = this.state.items.map((item) => ({
      ...item,
      isDisplayed: (
        tags.reduce(
          function tagReducer(result, tag) { // eslint-disable-line prefer-arrow-callback
            return result && (
              tag.isPriceFilter && item.price
                ? item.price <= (parseInt(tag.value, 10) * 100)
                : item.tags.includes(tag.value)
            );
          },
          true
        )
      )
    }));

    const actualItems = this.getSortedItems(filteredItems, this.state.sortBy)
      .map((item, index) => ({
        ...item,
        isDisplayed: item.isDisplayed &&
          (
            index + 1 <= (currentPage * this.state.productsPerPage) &&
            (index < filteredItems.length)
          )
      }));

    return {
      filteredItemsCount: filteredItems.filter((i) => i.isDisplayed).length,
      items: actualItems
    };
  }

  sortProducts(event) {
    const sortValue = event.target.value;
    window.appState.dispatch({
      type: 'EVENTS_CHAT_INTERACTION', // simply push new event
      payload: {
        eventName: 'GiftGuide Interaction - Sort by',
        eventAction: 'GiftGuide',
        trackFunctionArgs: 'GiftGuide Interaction',
        data: {
          sortBy: sortValue
        }
      }
    });
    this.setState((state) => ({
      sortBy: sortValue,
      items: this.getSortedItems(state.items, sortValue)
    }));
  }

  toggleMenu() {
    this.setState((state) => ({
      ...state,
      menuExpanded: !state.menuExpanded
    }));
  }

  loadProducts() {
    function handleError(response) {
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response.json();
    }
    fetch(
      urljoin(this.props.storeApiUrl, '/token'), { method: 'POST', credentials: 'include' }
    )
      .then(handleError)
      .then((_) => fetch(
        urljoin(
          this.props.storeApiUrl,
          '/products/',
          this.state.items.map((item) => item.productId).join(',')
        ),
        { credentials: 'include' }
      ))
      .then(handleError)
      .then((data) => {
        if (data.payload.length === 0) {
          throw Error();
        }
        window.appState.dispatch({
          type: 'EVENTS_CHAT_INTERACTION', // simply push new event
          payload: {
            eventName: 'GiftGuide Loaded',
            eventAction: 'GiftGuide',
            trackFunctionArgs: 'GiftGuide Interaction'
          }
        });
        this.setState((state) => ({
          ...state,
          products: data.payload.sort(Helpers.sortByDisplayed),
          items: state.items.map((item) => {
            const productData = data.payload.find((p) => p.id === item.productId);
            return {
              ...item,
              price: productData ? productData.price.amount : null,
              fetched: !!productData
            };
          }),
          fetchLoaded: true
        }));
        return this.setActiveTags(this.state.activeTags);
      })
      .catch((_) => {
        window.appState.dispatch({
          type: 'EVENTS_CHAT_INTERACTION', // simply push new event
          payload: {
            eventName: 'GiftGuide Failed',
            eventAction: 'GiftGuide',
            trackFunctionArgs: 'GiftGuide Interaction'
          }
        });
        this.setState((state) => ({
          ...state,
          products: [],
          fetchFailed: true,
          fetchLoaded: true
        }));
      });
  }

  loadMoreProducts() {
    window.appState.dispatch({
      type: 'EVENTS_CHAT_INTERACTION', // simply push new event
      payload: {
        eventName: 'GiftGuide Interaction - Load more',
        eventAction: 'GiftGuide',
        trackFunctionArgs: 'GiftGuide Interaction'
      }
    });
    this.setState((state) => ({
      ...state,
      currentPage: state.currentPage + 1,
      // eslint-disable-next-line react/no-access-state-in-setstate
      items: this.getFilteredItems(this.state.activeTags, state.currentPage + 1).items
    }));
  }

  render() {
    return (
      <section
        id={this.props.giftGuideID}
        className={this.state.fetchLoaded ? 'gift-guide-loaded' : 'gift-guide-loading'}
      >
        <GiftGuideHeader
          tags={this.props.tags}
          headerBackgroundHTHML={this.props.headerBackgroundHTHML}
          activeTags={this.state.activeTags}
          desktop={this.state.desktop}
          menuExpanded={this.state.menuExpanded}
          toggleMenu={this.toggleMenu}
          setActiveTag={this.setActiveTag}
          texts={this.props.texts}
          stickyHeader={this.props.stickyHeader}
          toggleTagsOnMobile={this.props.toggleTagsOnMobile}
        />

        <div className={'gift-guide-top'}>
          {this.props.showSorting && (
            <section className={'sort-by'}>
              <h4>{this.props.texts.sortBy}</h4>
              <select
                className={'price-sorting type-regular'}
                value={this.state.sortBy}
                onChange={this.sortProducts}
              >
                <option value={'default'}>Default</option>
                <option value={'l2h'}>Low to high</option>
                <option value={'h2l'}>High to low</option>
              </select>
            </section>
          )}
          {this.props.showSflLabel && (
            <section className={'save-for-later-label'}>
              {this.props.texts.sflLabelBefore}
              <Button
                dataInteraction={'save-for-later-product'}
                type={'button'}
                icon={'Heart'}
                classes={'dc-sfl-label-width'}
                fadeInOut
              />
              {this.props.texts.sflLabelAfter}
            </section>
          )}
        </div>


        {this.props.staticContents &&
          this.props.staticContents.map((item) => (
            <div
              key={item.name}
              className={`content ${item.name === this.state.activeTag ? 'active' : ''}`}
              dangerouslySetInnerHTML={{ __html: item.content }} /> // eslint-disable-line
          ))}

        {this.state.items.length < 1 &&
          <Loader />}

        <div id={'products'} className={'reactive'}>
          {this.state.items.length > 0 &&
            this.state.items.map((item) => (
              <GiftGuideItem
                item={item}
                key={item.productId}
                classes={`${item.extraClasses} ${item.isDisplayed ? 'show' : 'hideItem'}`}
                product={
                  this.state.products.length > 0 && item.fetched
                    ? this.state.products.find((p) => p.id === item.productId)
                    : null
                }
                displayedSwatchTypes={this.props.displayedSwatchTypes}
                storeApiUrl={this.props.storeApiUrl}
                cdnUrl={this.props.cdnUrl}
                giftGuideID={this.props.giftGuideID}
                fetchFailed={this.state.fetchFailed || !item.fetched}
                texts={this.props.texts}
                showCustomerRating={this.props.showCustomerRating}
                showSfl={this.props.showSfl}
                showSflLogged={this.props.showSflLogged}
                hideSavePrice={this.props.hideSavePrice}
              />
            ))}
        </div>

        {this.state.filteredItemsCount >
        (
          this.state.currentPage * this.state.productsPerPage) &&
          (this.state.products.length > 0 || this.state.items.length > 0) && (
          <button className={'purple-btn'} onClick={this.loadMoreProducts}>
            <span>{this.props.texts.loadMore}</span>
          </button>
        )}
      </section>
    );
  }
}

GiftGuide.propTypes = {
  activeTags: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string
    })
  ),
  inspirationContent: PropTypes.string,
  headerBackgroundHTHML: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.object).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  productsPerPage: PropTypes.number,
  staticContents: PropTypes.arrayOf(PropTypes.object),
  backupHTML: PropTypes.string,
  texts: PropTypes.shape({
    closeCategories: PropTypes.string.isRequired,
    openCategories: PropTypes.string.isRequired,
    loadMore: PropTypes.string.isRequired,
    sortBy: PropTypes.string.isRequired,
    sflLabelBefore: PropTypes.string.isRequired,
    sflLabelAfter: PropTypes.string.isRequired,
    save: PropTypes.string.isRequired
  }).isRequired,
  displayedSwatchTypes: PropTypes.arrayOf(PropTypes.string),
  giftGuideID: PropTypes.string.isRequired,
  storeApiUrl: PropTypes.string.isRequired,
  cdnUrl: PropTypes.string.isRequired,
  stickyHeader: PropTypes.bool,
  showSorting: PropTypes.bool,
  linkTarget: PropTypes.string,
  toggleTagsOnMobile: PropTypes.string,
  showCustomerRating: PropTypes.bool,
  showSfl: PropTypes.bool,
  showSflLabel: PropTypes.bool,
  showSflLogged: PropTypes.bool,
  hideSavePrice: PropTypes.bool
};

GiftGuide.defaultProps = {
  activeTags: [{ value: 'all' }],
  productsPerPage: 12,
  inspirationContent: null,
  staticContents: [],
  backupHTML: undefined,
  displayedSwatchTypes: [],
  stickyHeader: false,
  showSorting: false,
  linkTarget: '_blank',
  toggleTagsOnMobile: false,
  showCustomerRating: false,
  showSfl: false,
  showSflLogged: false,
  showSflLabel: false,
  hideSavePrice: false
};
